import React, { useState } from 'react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
dayjs.extend(relativeTime);

import { InputError } from '@riac/design-system';
import { CodeSent, IPhoneVerify } from 'src/components/Phones/PhoneVerify/PhoneVerify.i';
import { useFetch } from 'src/lib/FetchContext';
import { Input } from '@riac/design-system';
import { BoxButton } from '@riac/design-system';
import { TextButton } from '@riac/design-system';
import {
  PhoneNumberDisplay,
  PhoneVerifyContainer,
  PhoneVerifyPrompt,
  PhoneVerifyResult,
  SendCodeButton,
  VerifyButton,
  VerifySuccessMessage,
} from './PhoneVerify.style';

export const PhoneVerify = (props: IPhoneVerify) => {
  const { VerifiedID, Phone, PhoneID } = props.phone;
  const [inputError, setInputError] = useState(false);

  const [codeInput, setCodeInput] = useState('');
  const [codeSent, setCodeSent] = useState<CodeSent>(VerifiedID === 2 ? 'success' : false);
  const [verifySent, setVerifySent] = useState<CodeSent>(false);
  const [verifyError, setVerifyError] = useState('');

  const { fetch } = useFetch();

  async function sendVerification() {
    setCodeSent('sending');
    let formattedNum = Phone.replace(/[^0-9.]/g, '');
    if(Phone.trim().indexOf('+') == 0) {
      formattedNum = '+' + formattedNum;
    } else formattedNum = '+1' + formattedNum;
    const sendVerifyReq = await fetch('/api/account/phone/sendVerification', {
      credentials: 'include',
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        num: formattedNum,
        method: 'sms',
      }),
    });
    const sendVerifyRes = await sendVerifyReq.json();
    if(
      sendVerifyRes.status == 'denied'
      || sendVerifyRes.status == 'expired'
      || sendVerifyRes.error
    ) {
      setCodeSent('error');
      let resError = 'Unable to send verification to number provided. ';
      if(sendVerifyRes.code == 60205) {
        resError += 'Landlines are not supported.';
      }
      if(sendVerifyRes.limited) {
        resError
          += 'You have made too many requests. You may try again '
          + `${dayjs().millisecond(sendVerifyRes._msBeforeNext).fromNow()}`;
      }
      setVerifyError(resError);
      markRequestSent(PhoneID, 0);
      return;
    } else {
      markRequestSent(PhoneID, 2);
      setCodeSent('success');
    }
  }

  async function markRequestSent(pid, status) {
    await fetch(`/api/account/phone/markVerifySent/${pid}/${status}`, {
      credentials: 'include',
      method: 'PUT',
    });
    if(VerifiedID !== status) {
      props.updatePhoneList();
    }
  }

  async function checkVerification() {
    if(codeInput.length !== 4) {
      setInputError(true);
      return;
    }
    setInputError(false);
    setVerifySent('sending');
    setVerifyError('');
    let formattedNum = Phone.replace(/[^0-9.]/g, '');
    if(Phone.trim().indexOf('+') == 0) {
      formattedNum = '+' + formattedNum;
    } else formattedNum = '+1' + formattedNum;
    const checkVerifyReq = await fetch('/api/account/phone/checkVerification', {
      credentials: 'include',
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        num: formattedNum,
        code: codeInput,
        pid: PhoneID,
      }),
    });
    const checkVerifyRes = await checkVerifyReq.json();
    if(
      checkVerifyRes.status == 'denied'
      || checkVerifyRes.status == 'expired'
      || checkVerifyRes.error
    ) {
      setVerifySent('error');
      if(checkVerifyRes.limited) {
        setVerifyError(
          'There has been an error validating your device. You have made too many requests.'
            + ` You may try again ${dayjs().millisecond(checkVerifyRes._msBeforeNext).fromNow()}`
        );
        return;
      }
      setVerifyError(
        'There has been an error validating your device. It is possible the code you entered has '
          + 'expired. Please request a new code to be sent and verify it as soon as possible'
      );
      return;
    }
    if(checkVerifyRes.valid == false) {
      setVerifySent('error');
      setVerifyError('The code you have entered does not match our records. Please try again');
      return;
    }
    setVerifySent('success');
    props.updatePhoneList();
  }

  let num = '';
  if(Phone.trim().indexOf('+') == 0) {
    num = Phone.trim();
  } else num = Phone.replace(/[^0-9.]/g, '');
  const numberError
    = num.indexOf('+') == 0 || (num.length == 10 && num.indexOf('911') != 0) ? false : true;
  return (
    <PhoneVerifyContainer>
      <div>
        In order to receive text alert notifications, you must first verify that you are the owner
        of the device associated with this phone number*
      </div>
      {!codeSent && (
        <PhoneVerifyPrompt>
          <div>
            By clicking the button below, you authorize Rock Island Auction Company to send a
            one-time-use 4-digit verification code via sms to the following number:
          </div>
          <PhoneNumberDisplay>
            {num.indexOf('+') == 0
              ? num
              : `+1(${num.substring(0, 3)})${num.substring(3, 6)}-${num.substring(6, 10)}`
            }
          </PhoneNumberDisplay>
          {numberError && (
            <InputError
              error='
                To verify a phone, the number entered must be 10 digits and may not begin with the
                digits 911. Please edit your number and try again. If you are using a phone number
                registered outside the United States, please enter your country code along with your
                10 digit number. Example: +22 607 123 4567'
            />
          )}
          {!numberError && (
            <SendCodeButton isDisabled={numberError} onPress={sendVerification}>
              Send Code
            </SendCodeButton>
          )}
        </PhoneVerifyPrompt>
      )}
      {codeSent && (
        <PhoneVerifyResult>
          {(codeSent === 'sending' || verifySent === 'sending') && <div>Sending, Please Wait...</div>}
          {codeSent === 'error' && (
            <>
              <InputError error={verifyError} />
              <div>
                In order to verify and receive text alert notifications, the number entered must be
                registered to a mobile device. If you believe you have gotten this message in error,
                please double-check the number you entered and try again. If you need more
                assistance, please contact{' '}
                <TextButton to='/contact' blueText>customer service</TextButton>.
              </div>
            </>
          )}
          {codeSent === 'success' && (!verifySent || verifySent === 'error') && (
            <>
              <div>
                You should be receiving a text message with your 4 digit verification code shortly
              </div>
              <Input
                label='Please enter your 4 digit code below'
                value={codeInput}
                inverted
                onChange={(e) => setCodeInput(e.replace(/[^0-9]/g, ''))}
              />
              <VerifyButton onPress={checkVerification}>Verify</VerifyButton>
              {inputError ? (
                <InputError
                  error='Please enter the numeric 4 digit verification code that was sent to you with no
                  spaces, dashes, or other characters.'
                />
              ) : null}
              {verifySent === 'error' ? (
                <>
                  <InputError error={verifyError} />
                  {verifyError.includes('expired') && (
                    <BoxButton
                      onPress={() => {
                        setCodeSent(false);
                        setVerifySent(false);
                        setVerifyError('');
                        setInputError(false);
                        markRequestSent(PhoneID, 0);
                      }}
                    >
                      Reset
                    </BoxButton>
                  )}
                </>
              ) : null}
              <div>
                If it has been 10 minutes or more and you have not received a code, please double
                check the number you are attempting to verify and then
                {' '}
                <TextButton
                  onPress={() => {
                    setCodeSent(false);
                    setVerifySent(false);
                    setVerifyError('');
                    setInputError(false);
                    markRequestSent(PhoneID, 0);
                  }}
                  blueText
                >
                  <u>click here</u>
                </TextButton>
                {' '}
                to resend it. If you need more assistance, please contact
                {' '}
                <TextButton to='/contact' blueText>
                  <u>customer service</u>
                </TextButton>
              </div>
            </>
          )}
          {verifySent === 'success' && (
            <VerifySuccessMessage>
              Your number has been successfully verified
            </VerifySuccessMessage>
          )}
        </PhoneVerifyResult>
      )}
      <div>
        *Only numbers registered to a mobile device are eligible to receive text alert
        notifications. Standard Message & Data rates may apply.
      </div>
    </PhoneVerifyContainer>
  );
};
